// Nam Fruits — Product Detail Page custom element
// Handles variant selection, price updates, image swap, quantity, add-to-cart

class NFPdp extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.currentVariant = null;
  }

  connectedCallback() {
    const json = this.querySelector('[data-variants-json]');
    if (json) {
      try {
        this.variants = JSON.parse(json.textContent);
        this.currentVariant = this.variants.find(v => v.id == this.hiddenInput?.value) || this.variants[0];
      } catch (e) {
        console.warn('[nf-pdp] could not parse variants JSON', e);
      }
    }

    this.bindOptionSelectors();
    this.bindQuantityButtons();
    this.bindThumbnails();
  }

  get hiddenInput() {
    return this.querySelector('[data-pdp-variant-id]');
  }

  get priceAmount() {
    return this.querySelector('[data-pdp-price-amount]');
  }

  get titleEl() {
    return this.querySelector('[data-pdp-title]');
  }

  get priceCompare() {
    return this.querySelector('[data-pdp-price-compare]');
  }

  get addToCart() {
    return this.querySelector('[data-pdp-add-to-cart]');
  }

  get ctaText() {
    return this.querySelector('[data-pdp-cta-text]');
  }

  get mainImage() {
    return this.querySelector('[data-pdp-main-img]');
  }

  get options() {
    return Array.from(this.querySelectorAll('[data-pdp-option-name]'));
  }

  getSelectedValues() {
    // Index-matched: [option1, option2, option3]
    const values = [];
    this.options.forEach((optEl, idx) => {
      const selected = optEl.querySelector('.nf-pdp__option-value.is-selected');
      values[idx] = selected ? selected.dataset.value : null;
    });
    return values;
  }

  findMatchingVariant(values) {
    return this.variants.find(v => {
      return values.every((val, idx) => v.options[idx] === val);
    });
  }

  bindOptionSelectors() {
    this.querySelectorAll('[data-option-value]').forEach(btn => {
      btn.addEventListener('click', () => this.onOptionClick(btn));
    });
  }

  onOptionClick(btn) {
    const optionIndex = parseInt(btn.dataset.optionIndex, 10);
    const optionEl = this.options[optionIndex];
    if (!optionEl) return;

    optionEl.querySelectorAll('.nf-pdp__option-value').forEach(el => {
      el.classList.remove('is-selected');
      el.setAttribute('aria-checked', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');

    const values = this.getSelectedValues();
    const match = this.findMatchingVariant(values);
    if (match) {
      this.selectVariant(match);
    }
  }

  selectVariant(variant) {
    this.currentVariant = variant;
    if (this.hiddenInput) this.hiddenInput.value = variant.id;
    if (this.priceAmount) this.priceAmount.textContent = this.formatMoney(variant.price);
    const titleEl = this.querySelector('[data-pdp-title]');
    // 15/10/2026 (seo.md D4): data-static H1 lists every size ("180 g & 454 g") and must not
    // be rewritten to the selected variant — the H1 is the page's scope, not the selection.
    if (titleEl && titleEl.dataset.base && this.variants && this.variants.length > 1 && titleEl.dataset.static !== 'true') {
      // NBSP glue (khớp server render): "…Séchée 180 g" để "180 g"
      // không bao giờ rớt xuống dòng một mình.
      titleEl.textContent = titleEl.dataset.base + '\u00a0' + variant.title.replace(/(\d)g$/, '$1\u00a0g');
    }

    if (this.priceCompare) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.priceCompare.textContent = this.formatMoney(variant.compare_at_price);
        this.priceCompare.hidden = false;
      } else {
        this.priceCompare.hidden = true;
      }
    }

    if (this.addToCart && this.ctaText) {
      if (variant.available) {
        this.addToCart.removeAttribute('disabled');
        this.ctaText.textContent = window.nfTranslations?.addToCart || 'Add to Cart';
      } else {
        this.addToCart.setAttribute('disabled', '');
        this.ctaText.textContent = window.nfTranslations?.soldOut || 'Sold Out';
      }
    }

    if (variant.featured_image && this.mainImage) {
      const newSrc = variant.featured_image.src || '';
      if (newSrc) {
        // Swap src + strip srcset so browser uses the explicit src
        this.mainImage.src = newSrc;
        if (variant.featured_image.alt) this.mainImage.alt = variant.featured_image.alt; // 16/10 A7
        this.mainImage.removeAttribute('srcset');
      }
    }

    // Update URL with ?variant= for shareability
    if (variant.id && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  formatMoney(cents) {
    // Render y hệt server ({{ price | money }} -> "$16.99", KHÔNG "CAD"):
    // dùng shop.money_format bơm từ liquid, fallback "$x.xx".
    const amount = (cents / 100).toFixed(2);
    const fmt = window.nfTranslations?.moneyFormat;
    if (fmt && fmt.indexOf('{{') !== -1) {
      return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, amount).replace(/<[^>]*>/g, '').trim();
    }
    return `$${amount}`;
  }

  bindQuantityButtons() {
    this.querySelectorAll('.nf-pdp__qty-wrap').forEach(wrap => {
      const input = wrap.querySelector('[data-pdp-qty]');
      if (!input) return;
      wrap.querySelectorAll('.nf-pdp__qty-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const current = parseInt(input.value, 10) || 1;
          if (btn.dataset.action === 'increment') {
            input.value = current + 1;
          } else if (current > 1) {
            input.value = current - 1;
          }
        });
      });
    });
  }

  bindThumbnails() {
    // Danh sách ảnh lấy từ chính các thumb (đã đúng thứ tự product.images).
    // Không có thumb (chỉ 1 ảnh) thì vẫn cho zoom bằng ảnh chính.
    this.slides = Array.from(this.querySelectorAll('.nf-pdp__thumb'));
    this.index = 0;

    // Nạp sẵn TOÀN BỘ ảnh lớn để đổi ảnh gần như tức thì ("bấm mãi mới qua" khi
    // tải lúc bấm) — nhưng CHỈ sau khi trang load xong + trình duyệt rảnh
    // (16/10/2026, docs/seo.md B21): nạp ngay lúc vào trang thì 8 ảnh 1200 px
    // (~1 MB) tranh băng thông với ảnh chính = LCP trên mobile (trace: LCP 8,5 s).
    this.preloaded = [];
    const preloadAll = () => {
      this.slides.forEach((b) => {
        const u = b.dataset.image;
        if (!u) return;
        const im = new Image();
        im.decoding = 'async';
        im.src = u;
        this.preloaded.push(im);
      });
    };
    const whenIdle = () => {
      if (window.requestIdleCallback) window.requestIdleCallback(preloadAll, { timeout: 4000 });
      else setTimeout(preloadAll, 1500);
    };
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });

    this.slides.forEach((btn) => {
      btn.addEventListener('click', () => this.goTo(Number(btn.dataset.index || 0)));
    });

    const prev = this.querySelector('[data-pdp-prev]');
    const next = this.querySelector('[data-pdp-next]');
    if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
    if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

    const stage = this.querySelector('[data-pdp-stage]');
    const zoom = this.querySelector('[data-pdp-zoom]');
    if (this.mainImage) this.mainImage.addEventListener('click', () => this.openLightbox());
    if (zoom) zoom.addEventListener('click', (e) => { e.stopPropagation(); this.openLightbox(); });

    // Vuốt ngang trên mobile
    if (stage && this.slides.length > 1) {
      let x0 = null;
      stage.addEventListener('touchstart', (e) => { x0 = e.changedTouches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 45) this.goTo(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      }, { passive: true });
    }

    this.bindLightbox();
  }

  goTo(i) {
    if (!this.slides || !this.slides.length) return;
    const n = this.slides.length;
    const prevIndex = this.index;
    this.index = ((i % n) + n) % n;              // vòng tròn 2 chiều
    const btn = this.slides[this.index];
    const src = btn.dataset.image;
    if (src && this.mainImage) {
      // Hiệu ứng lướt: ảnh cũ trượt+mờ ra phía đối diện, ảnh mới trượt vào.
      const dir = (typeof prevIndex === 'number' && this.index !== prevIndex)
        ? (((this.index - prevIndex + n) % n) <= n / 2 ? 1 : -1)
        : 0;
      const img = this.mainImage;
      const swap = () => {
        img.src = src;
        if (btn.dataset.alt) img.alt = btn.dataset.alt; // 16/10 A7: alt follows the shown photo
        img.removeAttribute('srcset');
        img.classList.remove('is-out');
        img.style.setProperty('--nf-slide-from', (dir >= 0 ? 1 : -1) * 24 + 'px');
        img.classList.add('is-in');
        window.setTimeout(() => img.classList.remove('is-in'), 260);
      };
      if (dir === 0 || !img.classList) {
        swap();
      } else {
        img.style.setProperty('--nf-slide-to', (dir >= 0 ? -1 : 1) * 24 + 'px');
        img.classList.add('is-out');
        window.setTimeout(swap, 110);
      }
    }
    this.slides.forEach((b, k) => {
      if (k === this.index) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
    // giữ thumb đang chọn nằm trong tầm nhìn của hàng cuộn ngang
    if (btn.scrollIntoView) btn.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    if (this.lbOpen) this.paintLightbox();
  }

  bindLightbox() {
    this.lb = this.querySelector('[data-pdp-lightbox]');
    if (!this.lb) return;
    this.lbImg = this.querySelector('[data-pdp-lb-img]');
    this.lbCount = this.querySelector('[data-pdp-lb-count]');
    const close = this.querySelector('[data-pdp-lb-close]');
    const p = this.querySelector('[data-pdp-lb-prev]');
    const n = this.querySelector('[data-pdp-lb-next]');
    if (close) close.addEventListener('click', () => this.closeLightbox());
    if (p) p.addEventListener('click', () => this.goTo(this.index - 1));
    if (n) n.addEventListener('click', () => this.goTo(this.index + 1));
    this.lb.addEventListener('click', (e) => { if (e.target === this.lb) this.closeLightbox(); });
    this.onKey = (e) => {
      if (!this.lbOpen) return;
      if (e.key === 'Escape') this.closeLightbox();
      else if (e.key === 'ArrowLeft') this.goTo(this.index - 1);
      else if (e.key === 'ArrowRight') this.goTo(this.index + 1);
    };
    document.addEventListener('keydown', this.onKey);
  }

  paintLightbox() {
    if (!this.lbImg) return;
    const btn = this.slides && this.slides[this.index];
    this.lbImg.src = (btn && (btn.dataset.full || btn.dataset.image)) || (this.mainImage ? this.mainImage.src : '');
    if (this.lbCount && this.slides && this.slides.length) {
      this.lbCount.textContent = (this.index + 1) + ' / ' + this.slides.length;
    }
  }

  openLightbox() {
    if (!this.lb) return;
    this.lbOpen = true;
    this.lastFocus = document.activeElement;
    this.paintLightbox();
    this.lb.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    const close = this.querySelector('[data-pdp-lb-close]');
    if (close) close.focus();
  }

  closeLightbox() {
    if (!this.lb) return;
    this.lbOpen = false;
    this.lb.hidden = true;
    document.documentElement.style.overflow = '';
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  }
}

if (!customElements.get('nf-pdp')) {
  customElements.define('nf-pdp', NFPdp);
}

// live titles show "240 g" (space); shop titles may lack the space.
// NBSP glue: bind the space BEFORE the number too, so the weight never
// wraps onto a line of its own ("…Croustillantes / 240 g").
document.querySelectorAll('[data-pdp-title]').forEach((el) => {
  el.textContent = el.textContent.replace(/[\s\u00a0]*(\d+)[\s\u00a0]*g\s*$/, '\u00a0$1\u00a0g');
});

// [183] "You Might Also Like" phải loại CHÍNH sản phẩm đang xem;
// [187] link FR phải trỏ handle đã dịch (đỡ 1 hop 301).
// Section nam-fruits-products-showcase render từ block settings (link EN
// hardcode trong templates/product.json) nên vá tại client cho PDP;
// map EN<->FR khớp Translate & Adapt của store (3 SKU ổn định).
(function () {
  var FR_HANDLES = {
    'crispy-banana-chips-240g': 'chips-banane-croustillantes-240g',
    'crispy-jackfruit-chips-120g': 'chips-jacquier-croustillantes-120g',
    'dried-mango-slices': 'tranches-de-mangue-seche'
  };
  function toEn(handle) {
    for (var en in FR_HANDLES) {
      if (FR_HANDLES[en] === handle) return en;
    }
    return handle;
  }
  function handleOf(path) {
    var m = /\/products\/([^\/?#]+)/.exec(path || '');
    return m ? m[1] : '';
  }
  var isFr = /^\/fr(\/|$)/.test(location.pathname);
  var curEn = toEn(handleOf(location.pathname));
  if (!curEn) return;
  document.querySelectorAll('.nf-showcase__item').forEach(function (item) {
    var links = item.querySelectorAll('a[href*="/products/"]');
    if (!links.length) return;
    var itemEn = toEn(handleOf(links[0].getAttribute('href')));
    if (itemEn === curEn) {
      var grid = item.closest('.nf-showcase__grid');
      item.remove();
      if (grid && grid.querySelectorAll('.nf-showcase__item').length === 2) {
        grid.classList.add('nf-showcase__grid--two');
      }
      return;
    }
    if (isFr && FR_HANDLES[itemEn]) {
      links.forEach(function (a) {
        a.setAttribute('href', '/fr/products/' + FR_HANDLES[itemEn]);
      });
    }
  });
})();
